// strv-standard: bridge v1.1.0
// Master copy lives in strvcom/strv-it-hub /standard. Copy unchanged.
//
// How a tool talks to the IT Hub when it runs inside the hub's frame.
// Outside the hub, connectToHub() does nothing and the tool behaves as a
// normal standalone app.
//
// Protocol v1 — every message is an object { strv: 1, type, ... } sent with
// postMessage. The tool only accepts messages from the hub's origins; the
// hub only accepts messages from origins listed in its tool registry.
//
//   hub → tool   hello    { theme, user: { email, name }, path }   first contact
//                theme    { theme }                                theme changed
//                navigate { path }                                 go to this page
//                signout  { scope: 'hub' | 'everywhere' }          end the session
//                session  { token }                                5-minute sign-in proof (v1.1)
//   tool → hub   ready    { tool, version }                        handshake done
//                location { path, title }                          page changed
//                open     { tool, path }                           hand the user to another tool
//                command  { name: 'palette' }                      a hub shortcut was pressed
//
// session: the tool's backend verifies the token (a JWT signed by
// hub-runtime@strv-it-hub.iam.gserviceaccount.com, audience
// "strv-it-hub:<tool>", Google public keys at
// https://www.googleapis.com/service_accounts/v1/jwk/hub-runtime@strv-it-hub.iam.gserviceaccount.com)
// and signs the same person in with a Firebase custom token, remembering that
// the hub started the session.

export const PROTOCOL = 1;

export const HUB_ORIGINS = [
  'https://ithub.strv.io',
  'https://strv-it-hub.web.app',
  'https://strv-it-hub.firebaseapp.com',
];

/** @returns {boolean} true when this page is inside another page's frame. */
export function inFrame() {
  try {
    return window.self !== window.parent;
  } catch {
    return true;
  }
}

/**
 * Connect to the hub if we're inside it.
 *
 * @param {object} opts
 * @param {string} opts.tool        manifest id, e.g. 'gear'
 * @param {string} opts.version     the tool's version
 * @param {(theme: string) => void} [opts.onTheme]       apply a theme (usually theme.applyTheme with save:false)
 * @param {(path: string) => void} [opts.onNavigate]     route to a path inside the tool
 * @param {(scope: string) => void} [opts.onSignOut]     sign out if the hub started this session
 * @param {(token: string) => void} [opts.onSession]     exchange the hub's sign-in proof for a tool session
 * @param {(hello: object) => void} [opts.onHello]       first contact (user, initial path)
 * @param {string[]} [opts.extraOrigins]                 e.g. ['http://localhost:5173'] in development
 * @returns {null | { location: (path: string, title?: string) => void, open: (tool: string, path?: string) => void, connected: () => boolean }}
 */
export function connectToHub({ tool, version, onTheme, onNavigate, onSignOut, onSession, onHello, extraOrigins = [] }) {
  if (!inFrame()) return null;

  const allowed = new Set([...HUB_ORIGINS, ...extraOrigins]);
  let hubOrigin = null;

  const send = (type, payload = {}) => {
    if (!hubOrigin) return;
    window.parent.postMessage({ strv: PROTOCOL, type, ...payload }, hubOrigin);
  };

  window.addEventListener('message', (event) => {
    const msg = event.data;
    if (!allowed.has(event.origin) || !msg || msg.strv !== PROTOCOL) return;

    if (msg.type === 'hello') {
      hubOrigin = event.origin;
      document.documentElement.dataset.embed = 'hub';
      if (msg.theme) onTheme?.(msg.theme);
      onHello?.(msg);
      send('ready', { tool, version });
      return;
    }
    if (!hubOrigin || event.origin !== hubOrigin) return;
    if (msg.type === 'theme') onTheme?.(msg.theme);
    else if (msg.type === 'navigate') onNavigate?.(msg.path);
    else if (msg.type === 'signout') onSignOut?.(msg.scope);
    else if (msg.type === 'session' && typeof msg.token === 'string') onSession?.(msg.token);
  });

  // Hub shortcuts still work while focus is inside the tool.
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && hubOrigin) {
      e.preventDefault();
      send('command', { name: 'palette' });
    }
  });

  return {
    location: (path, title = document.title) => send('location', { path, title }),
    open: (target, path = '/') => send('open', { tool: target, path }),
    connected: () => hubOrigin !== null,
  };
}
